const STORAGE_KEY = 'zentrix_marketing_attribution';
const EXPIRY_DAYS = 90;

interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_adset?: string;
  utm_ad?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  li_fat_id?: string;
  landing_page_url?: string;
  first_seen_at?: string;
  stored_at?: string;
}

interface StoredAttribution {
  data: AttributionData;
  expires_at: number;
}

const TRACKED_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_adset', 'utm_ad', 'utm_content',
  'gclid', 'fbclid', 'li_fat_id',
] as const;

function readStored(): AttributionData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored: StoredAttribution = JSON.parse(raw);
    if (Date.now() > stored.expires_at) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored.data;
  } catch {
    return null;
  }
}

function writeStored(data: AttributionData) {
  const stored: StoredAttribution = {
    data: { ...data, stored_at: new Date().toISOString() },
    expires_at: Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

/**
 * Call on every page load (App mount).
 * Captures URL params and merges into localStorage with 90-day expiry.
 */
export function captureAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const incoming: Partial<AttributionData> = {};
    let hasParams = false;

    for (const key of TRACKED_PARAMS) {
      const val = params.get(key);
      if (val) {
        incoming[key] = val;
        hasParams = true;
      }
    }

    const existing = readStored();

    if (!existing) {
      // First visit
      const data: AttributionData = {
        ...incoming,
        landing_page_url: window.location.href,
        first_seen_at: new Date().toISOString(),
      };
      if (!hasParams) {
        data.utm_source = 'direct';
        data.utm_medium = 'none';
      }
      writeStored(data);
    } else if (hasParams) {
      // Subsequent visit with new params → partial update
      const merged: AttributionData = { ...existing, ...incoming };
      writeStored(merged);
    }
    // If no new params on subsequent visit, keep existing data untouched
  } catch (e) {
    console.warn('Attribution capture failed:', e);
  }
}

/**
 * Read stored attribution. Call on signup to persist to DB.
 */
export function getAttribution(): AttributionData {
  return readStored() || { utm_source: 'direct', utm_medium: 'none' };
}
